/**
 * Owner: Masa Bayu
 * File: use-pwa-phase-a.ts
 * Date:Time: 2026-09-22 06:02
 * Description: PWA-Phase-A hook — capability · install mode · shell update · BIP.
 */

import { useCallback, useEffect, useState } from 'react';
import { detectPwaCapabilities, type PwaCapabilitySnapshot } from './capability';
import { resolvePwaInstallMode, type PwaInstallModeView } from './install-mode';
import { checkQxreadShellUpdate, registerQxreadServiceWorker } from './pwa-register';
import {
  describeShellUpdateState,
  promptWaitingWorkerToActivate,
  type PwaShellUpdateState,
  type PwaShellUpdateView,
} from './shell-update';

type BeforeInstallPromptEventLike = Event & {
  readonly prompt: () => Promise<void>;
  readonly userChoice: Promise<{ readonly outcome: string }>;
};

export interface PwaPhaseAState {
  readonly capability: PwaCapabilitySnapshot;
  readonly install: PwaInstallModeView;
  readonly shellUpdate: PwaShellUpdateView;
  readonly bipAvailable: boolean;
  readonly installing: boolean;
  readonly standalone: boolean;
  readonly promptInstall: () => Promise<void>;
  readonly applyShellUpdate: () => Promise<void>;
  readonly dismissShellUpdate: () => void;
  readonly checkShellUpdate: () => Promise<void>;
}

/* §400 — sesetengah pelayar tidak menembak appinstalled (dialog OS dibatalkan
   senyap); selepas timeout ini kita kembali ke mod biasa supaya indikator
   "sedang dipasang" tidak tersangkut. */
const INSTALLING_TIMEOUT_MS = 90_000;

export function usePwaPhaseA(): PwaPhaseAState {
  const [bipSeen, setBipSeen] = useState(false);
  const [bipEvent, setBipEvent] = useState<BeforeInstallPromptEventLike | null>(null);
  const [softInstalled, setSoftInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [shellState, setShellState] = useState<PwaShellUpdateState>('current');
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [standalone, setStandalone] = useState(false);

  const capability = detectPwaCapabilities({
    beforeInstallPromptSeen: bipSeen,
    standaloneMedia: standalone,
  });
  const install = resolvePwaInstallMode({
    capability,
    softInstalledHint: softInstalled,
    installing,
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const media = window.matchMedia('(display-mode: standalone)');
    const sync = () => {
      setStandalone(media.matches);
      document.documentElement.dataset.pwaDisplay = media.matches ? 'standalone' : 'browser';
    };
    sync();
    media.addEventListener('change', sync);
    return () => {
      media.removeEventListener('change', sync);
    };
  }, []);

  useEffect(() => {
    const onBip = (event: Event) => {
      event.preventDefault();
      setBipSeen(true);
      setBipEvent(event as BeforeInstallPromptEventLike);
    };
    const onInstalled = () => {
      setInstalling(false);
      setSoftInstalled(true);
      setBipEvent(null);
      setBipSeen(false);
    };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const api =
        typeof navigator !== 'undefined' && 'serviceWorker' in navigator
          ? navigator.serviceWorker
          : null;
      const result = await registerQxreadServiceWorker(api);
      if (cancelled || !result.registered || result.registration === undefined) {
        return;
      }
      setRegistration(result.registration);
      if (result.registration.waiting !== null) {
        setShellState('update_available');
      }
      result.registration.addEventListener('updatefound', () => {
        const worker = result.registration?.installing;
        if (worker === null || worker === undefined) {
          return;
        }
        worker.addEventListener('statechange', () => {
          if (worker.state === 'installed' && navigator.serviceWorker.controller) {
            setShellState('update_available');
          }
        });
      });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }
    const onControllerChange = () => {
      setShellState('recovered');
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => {
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  useEffect(() => {
    if (!installing) {
      return;
    }
    const timer = window.setTimeout(() => {
      setInstalling(false);
    }, INSTALLING_TIMEOUT_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [installing]);

  const promptInstall = useCallback(async () => {
    if (bipEvent === null) {
      return;
    }
    await bipEvent.prompt();
    const choice = await bipEvent.userChoice;
    if (choice.outcome === 'accepted') {
      /* §400 — kekal jadi isyarat "installed" sekiranya pelayar tidak
         menembak appinstalled; mod installing menang sehingga timeout/event. */
      setInstalling(true);
      setSoftInstalled(true);
    }
    setBipEvent(null);
    setBipSeen(false);
  }, [bipEvent]);

  const applyShellUpdate = useCallback(async () => {
    setShellState('updating');
    const ok = await promptWaitingWorkerToActivate(registration);
    if (!ok) {
      setShellState('failed');
    }
  }, [registration]);

  const dismissShellUpdate = useCallback(() => {
    setShellState('current');
  }, []);

  const checkShellUpdate = useCallback(async () => {
    const waiting = await checkQxreadShellUpdate(registration);
    if (waiting) {
      setShellState('update_available');
    }
  }, [registration]);

  return {
    capability,
    install,
    shellUpdate: describeShellUpdateState(shellState),
    bipAvailable: bipEvent !== null,
    installing,
    standalone,
    promptInstall,
    applyShellUpdate,
    dismissShellUpdate,
    checkShellUpdate,
  };
}
